import { useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { Permissions } from 'types';
import { User } from 'types/api/users.type';

import { useAppSelector } from 'redux/hooks';
import { usersSelector } from 'redux/slices/users';

import { usersDataService } from 'services/users.service';

import { useFetch, useUserData } from 'hooks';

import { routes } from 'data/routes';

const useEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const form = useForm();
  const { isLoading, data } = useFetch(id ? usersDataService.getUser(id) : undefined, [id]);
  const users = useAppSelector(usersSelector.selectAll);

  const [user, userSet] = useState<User>();
  const [isShippingAddress, isShippingAddressSet] = useState<boolean>(false);
  const [existingUserId, existingUserIdSet] = useState<string>('');

  const onSubmit = async (fieldData: FieldValues) => {
    const { userData } = useUserData(fieldData, user);

    if (userData) {
      if (id) {
        usersDataService.putUser(id, userData).then((res) => {
          userSet(res.data);
          toast.success('User data successfully saved');
          form.reset(res.data);
        });
      } else {
        await usersDataService
          .postUser(userData)
          .then((res) => {
            toast.success('User created');
            navigate(routes.userView(res.data.id));
          })
          .catch((e) => {
            form.setError('apiFailed', { type: 'custom', message: 'Api failed' });

            if (e.response.data.apierror.message.includes('already exists')) {
              const email = e.response.data.apierror.message
                .split(' ')
                .find((el: string) => el.includes('@'));
              const userId = users.find((el) => el.email === email)?.id;
              if (userId?.length) existingUserIdSet(userId);
            }

            form.clearErrors();
          });
      }
    }
  };

  const values = useMemo(
    () => ({
      isShippingAddress,
      isShippingAddressSet,
      user,
      userSet,
      form,
      onSubmit: form.handleSubmit(onSubmit),
    }),
    [isShippingAddress, user, form]
  );

  useEffect(() => {
    if (data) {
      userSet(data);
    }
  }, [data]);

  useEffect(() => {
    if (user) {
      Object.entries(user).forEach(([key, value]) => form?.setValue(key, value));

      if (user.shippingAddress) {
        isShippingAddressSet(true);
        Object.entries(user.shippingAddress).forEach(([key, value]) =>
          form?.setValue(`shippingAddress.${key}`, value)
        );
      }

      if (user.billingAddress) {
        Object.entries(user.billingAddress).forEach(([key, value]) =>
          form?.setValue(`billingAddress.${key}`, value)
        );
      }

      if (user.userType) {
        form.setValue('userType', user.userType.id);
      }

      // if (user.zone) {
      //   form.setValue('zone', undefined);
      // }

      if (user.userType) {
        form.setValue('userType', { id: user.userType.id });
      }

      if (user.priceList) {
        form.setValue('priceList', { id: user.priceList.id });
      }

      if (user.permissions && user.permissions.includes(Permissions.CATALOGUE)) {
        form.setValue('CATALOGUE', true);
      }
    }
  }, [user]);

  return {
    values,
    isShippingAddress,
    user,
    isLoading,
    onSubmit,
    id,
    existingUserId,
    existingUserIdSet,
  };
};

export { useEdit };
